import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * CruiseCabinRate - per-Cruise cabin rate cards (clone of HotelInventory, B4.12
 * pattern). One row per (Cruise, CruiseCabin?, RoomType, MealPlan,
 * OccupancyType, validity window) tuple. Sub-resource of Cruise; mounted at
 * `/cruises/:cruiseId/rates`.
 *
 * Unlike HotelInventory, `roomTypeId`/`mealPlanId`/`occupancyTypeId` are
 * required on the Prisma model (not nullable) - the rate always pins a
 * concrete combination even when it is not linked to a specific cabin.
 *
 * Prices are plain decimal amounts (up to 2 places) in the row's own
 * currency (`currencyId`) - no minor units / "paise".
 */

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

/** Meal option a cabin rate sells with - per-person per-night supplement. */
export const CruiseCabinRateMealOptionSchema = z.object({
  id: UuidSchema,
  mealPlanId: UuidSchema,
  mealPlanCode: z.string().nullable(),
  mealPlanName: z.string().nullable(),
  perAdult: z.number(),
  perChild: z.number(),
  isActive: z.boolean(),
});
export type CruiseCabinRateMealOption = z.infer<typeof CruiseCabinRateMealOptionSchema>;

export const CruiseCabinRateMealOptionInputSchema = z.object({
  mealPlanId: UuidSchema,
  perAdult: PriceSchema.optional(),
  perChild: PriceSchema.optional(),
  isActive: z.boolean().optional(),
});
export type CruiseCabinRateMealOptionInput = z.infer<typeof CruiseCabinRateMealOptionInputSchema>;

export const CruiseCabinRateSchema = z.object({
  id: UuidSchema,
  cruiseId: UuidSchema,
  cruiseCabinId: UuidSchema.nullable(),
  cruiseCabinName: z.string().nullable(),
  roomTypeId: UuidSchema,
  roomTypeName: z.string().nullable(),
  mealPlanId: UuidSchema,
  mealPlanName: z.string().nullable(),
  occupancyTypeId: UuidSchema,
  occupancyTypeName: z.string().nullable(),
  /** Base adults the rate covers (occupancy type paxCount). Null = default 2. */
  occupancyPaxCount: z.number().int().nullable(),
  /** Explicit base occupancy (adults) the rate covers - a free integer that
   *  overrides the 1-3 occupancy-type paxCount. Null falls back to
   *  `occupancyPaxCount`, else 2. */
  baseOccupancy: z.number().int().nullable(),
  /** Live FX rate from this rate's currency to INR at list-time (1 for INR). */
  fxRateToInr: z.number().default(1),
  validityFrom: z.string(),
  validityTo: z.string(),
  /** Day-of-week applicability mask. bit0=Sun..bit6=Sat; 127 = all days. */
  weekdayMask: z.number().int(),
  currencyId: UuidSchema,
  currencyCode: z.string().nullable(),
  adultPrice: z.number(),
  childWithBedPrice: z.number(),
  childWithoutBedPrice: z.number(),
  infantPrice: z.number(),
  extraAdultPrice: z.number(),
  extraChildPrice: z.number(),
  extraBedChildPrice: z.number(),
  fixedCabinCost: z.number(),
  /** Sold-out / unavailable dates within the window (YYYY-MM-DD). */
  blackoutDates: z.array(z.string()),
  mealOptions: z.array(CruiseCabinRateMealOptionSchema),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CruiseCabinRate = z.infer<typeof CruiseCabinRateSchema>;

export const CruiseCabinRateListQuerySchema = PaginationQuerySchema.extend({
  cruiseCabinId: UuidSchema.optional(),
  roomTypeId: UuidSchema.optional(),
  mealPlanId: UuidSchema.optional(),
  occupancyTypeId: UuidSchema.optional(),
  /** Filter to rows whose validity window contains this date (YYYY-MM-DD). */
  onDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type CruiseCabinRateListQuery = z.infer<typeof CruiseCabinRateListQuerySchema>;

export const CruiseCabinRateListResponseSchema = paginatedResponseSchema(CruiseCabinRateSchema);
export type CruiseCabinRateListResponse = z.infer<typeof CruiseCabinRateListResponseSchema>;

export const CruiseCabinRateInputSchema = z.object({
  cruiseCabinId: UuidSchema.nullable().optional(),
  roomTypeId: UuidSchema,
  mealPlanId: UuidSchema,
  occupancyTypeId: UuidSchema,
  /** Base occupancy (adults) the rate covers - free integer; null clears it. */
  baseOccupancy: z.number().int().min(1).max(40).nullable().optional(),
  validityFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validityTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** bit0=Sun..bit6=Sat; defaults to all days (127) when omitted. */
  weekdayMask: z.number().int().min(0).max(127).optional(),
  currencyId: UuidSchema,
  adultPrice: PriceSchema.optional(),
  childWithBedPrice: PriceSchema.optional(),
  childWithoutBedPrice: PriceSchema.optional(),
  infantPrice: PriceSchema.optional(),
  extraAdultPrice: PriceSchema.optional(),
  extraChildPrice: PriceSchema.optional(),
  extraBedChildPrice: PriceSchema.optional(),
  fixedCabinCost: PriceSchema.optional(),
  /** Sold-out / unavailable dates within the window (YYYY-MM-DD). */
  blackoutDates: z
    .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
    .max(366)
    .optional(),
  /** Full replacement set of meal options (sync semantics). */
  mealOptions: z.array(CruiseCabinRateMealOptionInputSchema).optional(),
  isActive: z.boolean().optional(),
});
export type CruiseCabinRateInput = z.infer<typeof CruiseCabinRateInputSchema>;

/** `to` must be on or after `from`. Dates are 'YYYY-MM-DD' so a lexicographic
 *  compare is chronological. Only enforced when both bounds are present (an
 *  update may send neither / one; the form always sends both). */
const validityOrdered = (v: { validityFrom?: string; validityTo?: string }): boolean =>
  v.validityFrom === undefined || v.validityTo === undefined || v.validityTo >= v.validityFrom;
const validityOrderedError = {
  message: 'Validity "to" date must be on or after the "from" date',
  path: ['validityTo'],
};

export const CreateCruiseCabinRateRequestSchema = CruiseCabinRateInputSchema.refine(
  validityOrdered,
  validityOrderedError,
);
export type CreateCruiseCabinRateRequest = z.infer<typeof CreateCruiseCabinRateRequestSchema>;

export const UpdateCruiseCabinRateRequestSchema = CruiseCabinRateInputSchema.partial().refine(
  validityOrdered,
  validityOrderedError,
);
export type UpdateCruiseCabinRateRequest = z.infer<typeof UpdateCruiseCabinRateRequestSchema>;
